// Relative imports, not the '@' alias: jest has no moduleNameMapper for it
// (see useGeneratedCourse.ts).
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {QueryClient, useMutation, useQueryClient} from '@tanstack/react-query';

import type {CoverCoach} from './types';

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

/**
 * Upload a coach's photo. A bare fetch, not ChessRestClient: the browser must
 * set the multipart Content-Type itself, because only it knows the boundary —
 * the same reason the course upload uses a bare XHR.
 */
export async function uploadCoachPhoto(coach: string, photo: Blob): Promise<CoverCoach> {
  const form = new FormData();
  form.append('coach', coach);
  form.append('photo', photo);
  const base = (ChessRestClient.defaults.baseURL || '').replace(/\/$/, '');
  const response = await fetch(`${base}/video_learning/coach-photo/`, {
    method: 'POST',
    headers: await authHeader(),
    body: form,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || 'Could not upload the photo.');
  return body.coach as CoverCoach;
}

export type CoachPhotoPatch = {
  coach: string;
  crop?: {x: number; y: number; zoom: number};
  display_title?: string;
  display_name?: string;
};

export async function patchCoachPhoto(body: CoachPhotoPatch): Promise<CoverCoach> {
  const r = await ChessRestClient.patch('/video_learning/coach-photo/', body, {
    headers: await authHeader(),
  });
  return r.data.coach as CoverCoach;
}

export async function removeCoachPhoto(coach: string): Promise<CoverCoach> {
  const r = await ChessRestClient.delete('/video_learning/coach-photo/', {
    headers: await authHeader(),
    params: {coach},
  });
  return r.data.coach as CoverCoach;
}

export type CoverChoice = {page_id?: number | null; hook?: string | null};

export async function patchCourseCover(bookUuid: string, body: CoverChoice) {
  const r = await ChessRestClient.patch(`/video_learning/course/${bookUuid}/cover/`, body, {
    headers: await authHeader(),
  });
  return r.data.cover as CoverChoice | null;
}

/** The course and the list both carry the cover; both are refetched. */
export function afterCoverChange(qc: QueryClient, bookUuid: string) {
  qc.invalidateQueries(['generated-course', bookUuid]);
  qc.invalidateQueries(['generated-course-list']);
}

/** The reviewer's thumbnail controls for one course. */
export function useCoverControls(bookUuid: string) {
  const qc = useQueryClient();
  const onSuccess = () => afterCoverChange(qc, bookUuid);
  return {
    upload: useMutation(
      ({coach, photo}: {coach: string; photo: Blob}) => uploadCoachPhoto(coach, photo),
      {onSuccess},
    ),
    saveCoach: useMutation(patchCoachPhoto, {onSuccess}),
    removePhoto: useMutation(removeCoachPhoto, {onSuccess}),
    saveCover: useMutation((body: CoverChoice) => patchCourseCover(bookUuid, body), {onSuccess}),
  };
}
